import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as Constants from '@emi/shared/constants';
import { selectSkuSearch, SkuSearchState } from '@emi/shared/state/sku-search';
import {
    AttributesService,
    AttributeTypesService,
    BrandsService,
    CategoryService,
    CountryService,
    ExchangeRatesService,
    ExtractionsService,
    PromotionsService,
    RetailersService,
    RetailerTypesService,
    SuppliersService,
} from '@emi/shared/data-access';
import {
    AttributeDto,
    AttributeTypeDto,
    Brand,
    BrandDto,
    Category,
    CategoryDto,
    ConfigureData,
    CountryDto,
    DateRange,
    DynamicExchangeRate,
    Extraction,
    ExtractionDto,
    FileTypes,
    Filters,
    IndustryCategory,
    MinMaxRange,
    PromotionDto,
    RetailerDto,
    RetailerTypeDto,
    SkuRequestFilter,
    SubBrand,
    SupplierDto,
} from '@emi/shared/models';
import { CountryState, getCountryState, getExtractionState, getSpecifiedExtraction } from '@emi/shared/state/filters';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import * as extractionActions from './extraction-actions';
import { DataExtractionState } from './extraction-reducer';

@Injectable()
export class ExtractionEffects {
    private searchTerm: string;
    private includeSearchPhrase: boolean | undefined;

    constructor(
        private actions$: Actions,
        private extractionService: ExtractionsService,
        private attributesService: AttributesService,
        private attributeTypesService: AttributeTypesService,
        private brandsService: BrandsService,
        private categoriesService: CategoryService,
        private countriesService: CountryService,
        private retailersService: RetailersService,
        private retailerTypesService: RetailerTypesService,
        private suppliersService: SuppliersService,
        private promotionsService: PromotionsService,
        private exchangeRatesService: ExchangeRatesService,

        private store: Store<DataExtractionState>,
        private skuSearchStore: Store<SkuSearchState>,
        private router: Router,
        private countryStore: Store<CountryState>
    ) {
        this.skuSearchStore
            .pipe(select(selectSkuSearch))
            .subscribe(
                (skuSearchState) => (
                    (this.searchTerm = skuSearchState.searchTerm), (this.includeSearchPhrase = skuSearchState.includeSearchPhrase)
                )
            );
    }

    public extraction: ExtractionDto;

    postExtraction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.postExtractions),
            switchMap(() => {
                this.store.pipe(select(getExtractionState)).subscribe((x) => {
                    const searchTerm = x.selectedConfiguration.searchPhrase === '' ? this.searchTerm : x.selectedConfiguration.searchPhrase;
                    this.extraction = new ExtractionDto(
                        x.title,
                        x.description,
                        this.includeSearchPhrase ? searchTerm : '',
                        x.selectedConfiguration.frequency,
                        x.selectedConfiguration.dataType,
                        this.getFilterIds(x.selectedFilters),
                        x.selectedConfiguration.pricePoint,
                        x.selectedConfiguration.currency,
                        x.selectedConfiguration.fileType,
                        x.selectedConfiguration.startDate,
                        x.selectedConfiguration.endDate,
                        x.selectedConfiguration.includeAttributesFile
                    );

                    const { skuFilters } = this.extraction;

                    if (!skuFilters.countryIds || skuFilters.countryIds.length === 0) {
                        this.countryStore.select(getCountryState).subscribe(({ countries }) => {
                            skuFilters.countryIds = countries.map(({ id }) => id);
                        });
                    }
                });
                return this.extractionService.createExtraction(this.extraction).pipe(
                    map(() => {
                        return extractionActions.postExtractionsSuccess();
                    }),
                    tap(() => this.router.navigate([Constants.Routes.SUCCESS_ROUTE]))
                );
            }),
            catchError((error: HttpErrorResponse) => {
                return of(extractionActions.getExtractionsFailure({ error }));
            })
        )
    );

    getExtraction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.getExtractions),
            map(() => {
                return extractionActions.isLoadingExtractions();
            })
        )
    );

    fetchExtractions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.fetchExtractions),
            switchMap(() => {
                return this.extractionService.getExtractions().pipe(
                    map((data: Extraction[]) => {
                        data.forEach(function (value) {
                            if (value.currency === undefined) {
                                const foundIndex = data.findIndex((x) => x.id == value.id);
                                data[foundIndex].currency = Constants.GlobalVariables.LOCAL_CURRENCY;
                            }
                        });
                        return extractionActions.getExtractionsSuccess({
                            extractions: data,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(extractionActions.getExtractionsFailure({ error }));
                    })
                );
            })
        )
    );

    getFilterNames$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.getFilterNames),
            map(({ extraction }) => {
                return extractionActions.isLoadingExtractionFilterNames({
                    extractionId: extraction.id,
                    isLoading: true,
                });
            })
        )
    );

    getAttributeFilterNames$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.getFilterNames),
            filter(({ extraction }) => extraction.skuFilters.attributeIds?.length > 0),
            switchMap(({ extraction }) => {
                const filterIds = {
                    filters: { attributeIds: extraction.skuFilters.attributeIds },
                };
                return this.attributesService.getAttributes(filterIds).pipe(
                    map((data: AttributeDto) =>
                        extractionActions.getFilterNamesSuccess({
                            names: data.results.map((attribute) => attribute.value),
                            extractionId: extraction.id,
                            filterType: 'attributes',
                        })
                    )
                );
            }),
            catchError((error: HttpErrorResponse) => {
                return of(
                    extractionActions.getFilterNamesFailure({
                        error,
                        filterType: 'attributes',
                    })
                );
            })
        )
    );

    getAttributeTypeFilterNames$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.getFilterNames),
            filter(({ extraction }) => extraction.skuFilters.attributeGroupIds?.length > 0),
            switchMap(({ extraction }) => {
                const filterIds = {
                    filters: {
                        attributeGroupIds: extraction.skuFilters.attributeGroupIds,
                    },
                };
                return this.attributeTypesService.getAttributeTypes(filterIds).pipe(
                    map((data: AttributeTypeDto) =>
                        extractionActions.getFilterNamesSuccess({
                            names: data.results.map((attributeType) => attributeType.name),
                            extractionId: extraction.id,
                            filterType: 'attributeTypes',
                        })
                    )
                );
            }),
            catchError((error: HttpErrorResponse) => {
                return of(
                    extractionActions.getFilterNamesFailure({
                        error,
                        filterType: 'attributeTypes',
                    })
                );
            })
        )
    );

    getBrandFilterNames$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.getFilterNames),
            filter(({ extraction }) => extraction.skuFilters.brandIds?.length > 0 || extraction.skuFilters.subBrandIds?.length > 0),
            switchMap(({ extraction }) => {
                const filterIds = {
                    filters: {
                        countryIds: extraction.skuFilters.countryIds,
                        categoryIds: extraction.skuFilters.categoryIds,
                        brandIds: extraction.skuFilters.brandIds,
                        subBrandIds: extraction.skuFilters.subBrandIds,
                    },
                };
                return this.brandsService.getBrands(filterIds).pipe(
                    map((data: BrandDto) => {
                        const brandNames: string[] = [];
                        data.results.forEach((brand) => {
                            brandNames.push(brand.brandName);
                            brand?.subBrands?.forEach((subBrand) => {
                                if (extraction.skuFilters.subBrandIds.some((value) => value == subBrand.subBrandId)) {
                                    brandNames.push(subBrand.subBrandName);
                                }
                            });
                        });
                        return extractionActions.getFilterNamesSuccess({
                            names: brandNames,
                            extractionId: extraction.id,
                            filterType: 'brands',
                        });
                    })
                );
            }),
            catchError((error: HttpErrorResponse) => {
                return of(
                    extractionActions.getFilterNamesFailure({
                        error,
                        filterType: 'brands',
                    })
                );
            })
        )
    );

    getCategoryFilterNames$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.getFilterNames),
            filter(({ extraction }) => extraction.skuFilters.categoryIds?.length > 0),
            switchMap(({ extraction }) => {
                let allIdustryCategories: Array<IndustryCategory> = [];
                const filterIds = {
                    filters: { categoryIds: extraction.skuFilters.categoryIds },
                };
                return this.categoriesService.getCategories(filterIds).pipe(
                    tap((categories) => {
                        allIdustryCategories = this.getIndustryCategory(extraction.skuFilters.categoryIds, categories?.results);
                    }),
                    map((data: CategoryDto) => {
                        const categoryNames = this.getExtractionSelectedCategoryNames(data.results, allIdustryCategories);

                        return extractionActions.getFilterNamesSuccess({
                            names: categoryNames,
                            extractionId: extraction.id,
                            filterType: 'categories',
                        });
                    })
                );
            }),
            catchError((error: HttpErrorResponse) => {
                return of(
                    extractionActions.getFilterNamesFailure({
                        error,
                        filterType: 'categories',
                    })
                );
            })
        )
    );

    getCountryFilterNames$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.getFilterNames),
            filter(({ extraction }) => extraction.skuFilters.countryIds?.length > 0),
            switchMap(({ extraction }) => {
                const filterIds = {
                    filters: { countryIds: extraction.skuFilters.countryIds },
                };
                return this.countriesService.getCountries(filterIds).pipe(
                    map((data: CountryDto) => {
                        const countryNames = data.results
                            .filter((country) => extraction.skuFilters.countryIds.includes(country.id))
                            .map((country) => country.name);
                        return extractionActions.getFilterNamesSuccess({
                            names: countryNames,
                            extractionId: extraction.id,
                            filterType: 'countries',
                        });
                    })
                );
            }),
            catchError((error: HttpErrorResponse) => {
                return of(
                    extractionActions.getFilterNamesFailure({
                        error,
                        filterType: 'countries',
                    })
                );
            })
        )
    );

    getRetailerFilterNames$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.getFilterNames),
            filter(({ extraction }) => extraction.skuFilters.retailerIds?.length > 0),
            switchMap(({ extraction }) => {
                const filterIds = {
                    filters: { retailerIds: extraction.skuFilters.retailerIds },
                };
                return this.retailersService.getRetailers(filterIds).pipe(
                    map((data: RetailerDto) => {
                        return extractionActions.getFilterNamesSuccess({
                            names: data.results.map((retailer) => retailer.name),
                            extractionId: extraction.id,
                            filterType: 'retailers',
                        });
                    })
                );
            }),
            catchError((error: HttpErrorResponse) => {
                return of(
                    extractionActions.getFilterNamesFailure({
                        error,
                        filterType: 'retailers',
                    })
                );
            })
        )
    );

    getRetailerTypeFilterNames$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.getFilterNames),
            filter(({ extraction }) => extraction.skuFilters.retailerTypeIds?.length > 0),
            switchMap(({ extraction }) => {
                const filterIds = {
                    filters: { retailerTypeIds: extraction.skuFilters.retailerTypeIds },
                };
                filter(() => extraction.skuFilters.retailerTypeIds.length > 0);
                return this.retailerTypesService.getRetailerTypes(filterIds).pipe(
                    map((data: RetailerTypeDto) => {
                        return extractionActions.getFilterNamesSuccess({
                            names: data.results.map((retailerType) => retailerType.name),
                            extractionId: extraction.id,
                            filterType: 'retailerTypes',
                        });
                    })
                );
            }),
            catchError((error: HttpErrorResponse) => {
                return of(
                    extractionActions.getFilterNamesFailure({
                        error,
                        filterType: 'retailerTypes',
                    })
                );
            })
        )
    );

    getSupplierFilterNames$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.getFilterNames),
            filter(({ extraction }) => extraction.skuFilters.supplierIds?.length > 0),
            switchMap(({ extraction }) => {
                const filterIds = {
                    filters: { supplierIds: extraction.skuFilters.supplierIds },
                };
                return this.suppliersService.getSuppliers(filterIds).pipe(
                    map((data: SupplierDto) => {
                        return extractionActions.getFilterNamesSuccess({
                            names: data.results.map((supplier) => supplier.name),
                            extractionId: extraction.id,
                            filterType: 'suppliers',
                        });
                    })
                );
            }),
            catchError((error: HttpErrorResponse) => {
                return of(
                    extractionActions.getFilterNamesFailure({
                        error,
                        filterType: 'suppliers',
                    })
                );
            })
        )
    );

    getPromotionsFilterNames$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.getFilterNames),
            filter(({ extraction }) => extraction.skuFilters.promotionTypeIds?.length > 0),
            switchMap(({ extraction }) => {
                const filterIds = {
                    filters: {
                        promotionTypeIds: extraction.skuFilters.promotionTypeIds,
                        countryIds: extraction.skuFilters.countryIds,
                        categoryIds: extraction.skuFilters.categoryIds,
                    },
                };
                return this.promotionsService.getPromotions(filterIds).pipe(
                    map((data: PromotionDto) => {
                        return extractionActions.getFilterNamesSuccess({
                            names: data.results
                                .filter((promotion) => extraction.skuFilters.promotionTypeIds.includes(promotion.id))
                                .map((promotion) => promotion.name),
                            extractionId: extraction.id,
                            filterType: 'promotions',
                        });
                    })
                );
            }),
            catchError((error: HttpErrorResponse) => {
                return of(
                    extractionActions.getFilterNamesFailure({
                        error,
                        filterType: 'promotions',
                    })
                );
            })
        )
    );

    stopLoadingExtractionFilterNames$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.getFilterNamesSuccess),
            concatLatestFrom(({ extractionId }) => this.store.select(getSpecifiedExtraction(extractionId))),
            filter(([, extraction]) => {
                let idCount = 0;
                let nameCount = 0;
                const skuFilter: [string, []][] = Object.entries(extraction?.skuFilters ?? {});
                for (const [Property, Array] of skuFilter) {
                    if (Array.length > 0 && Constants.SearchableSkuFilters.includes(Property)) idCount++;
                }
                const filterNames: [string, string[]][] = Object.entries(extraction?.filterNames ?? {});
                for (const [, Array] of filterNames) {
                    if (Array.length > 0) nameCount++;
                }
                return idCount === nameCount;
            }),
            map(([action]) => {
                return extractionActions.isLoadingExtractionFilterNames({
                    extractionId: action.extractionId,
                    isLoading: false,
                });
            })
        )
    );

    navigateToFilters$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.navigateToFilters),
            concatLatestFrom(() => this.store.select(getExtractionState)),
            filter(([action, extraction]) => {
                let idCount = 0;
                const skuFilter: [string, []][] = Object.entries(
                    extraction?.extractions?.find((extraction) => extraction.id === action.extractionId)?.skuFilters ?? {}
                );
                for (const [Property, Array] of skuFilter) {
                    if (Array && Array.length > 0 && Constants.SearchableSkuFilters.includes(Property)) idCount++;
                }
                // commenting this as of now for PBI 174048.
                // const filters: [string, string[]][] = Object.entries(extraction?.selectedFilters ?? {});
                // for (const [, Array] of filters) {
                //     if (Array && Array.length > 0) nameCount++;
                // }
                //return idCount === nameCount;
                return idCount > 0;
            }),
            map(() =>
                extractionActions.isLoadingSelections({
                    isLoading: false,
                })
            ),
            tap(() => this.router.navigate([Constants.Routes.FILTER_DATA_ROUTE]))
        )
    );

    getFilterSelections$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.getFilterSelections),
            switchMap(({ extraction }) =>
                of(
                    extractionActions.isLoadingSelections({
                        isLoading: true,
                    }),
                    extractionActions.addConfigureDataSelection({
                        configureData: new ConfigureData(
                            extraction.startDate,
                            extraction.endDate,
                            extraction.searchPhrase,
                            extraction.pricePer,
                            extraction.frequency,
                            extraction.viewBy,
                            extraction.currency,
                            extraction.timeSeriesData,
                            extraction.includeAttributesFile,
                            extraction.fileType,
                            extraction.skuFilters.firstSeenPriceRange,
                            extraction.skuFilters.lastSeenPriceRange,
                            extraction.skuFilters.volumeRange,
                            extraction.skuFilters.weightRange,
                            extraction.skuFilters.countRange,
                            extraction.exchangeRates
                        ),
                    })
                )
            )
        )
    );

    getAttributeFilterSelections$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.getFilterSelections),
            filter(({ extraction }) => extraction.skuFilters.attributeIds?.length > 0),
            switchMap(({ extraction }) => {
                const filterIds = {
                    filters: { attributeIds: extraction.skuFilters.attributeIds },
                };
                return this.attributesService.getAttributes(filterIds).pipe(
                    switchMap((data: AttributeDto) =>
                        of(
                            extractionActions.getAttributeFiltersSuccess({
                                attributes: data.results,
                            }),
                            extractionActions.navigateToFilters({ extractionId: extraction.id })
                        )
                    )
                );
            }),
            catchError((error: HttpErrorResponse) => {
                return of(
                    extractionActions.getFilterNamesFailure({
                        error,
                        filterType: 'attributes',
                    })
                );
            })
        )
    );

    getAttributeTypeFilterSelections$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.getFilterSelections),
            filter(({ extraction }) => extraction.skuFilters.attributeGroupIds?.length > 0),
            switchMap(({ extraction }) => {
                const filterIds = {
                    filters: {
                        attributeGroupIds: extraction.skuFilters.attributeGroupIds,
                    },
                };
                return this.attributeTypesService.getAttributeTypes(filterIds).pipe(
                    switchMap((data: AttributeTypeDto) =>
                        of(
                            extractionActions.getAttributeTypeFiltersSuccess({
                                attributeTypes: data.results,
                            }),
                            extractionActions.navigateToFilters({ extractionId: extraction.id })
                        )
                    )
                );
            }),
            catchError((error: HttpErrorResponse) => {
                return of(
                    extractionActions.getFilterNamesFailure({
                        error,
                        filterType: 'attributeTypes',
                    })
                );
            })
        )
    );

    getBrandFilterSelections$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.getFilterSelections),
            filter(({ extraction }) => extraction.skuFilters.brandIds?.length > 0 || extraction.skuFilters.subBrandIds?.length > 0),
            switchMap(({ extraction }) => {
                const filterIds = {
                    filters: {
                        countryIds: extraction.skuFilters.countryIds,
                        categoryIds: extraction.skuFilters.categoryIds,
                        brandIds: extraction.skuFilters.brandIds,
                        subBrandIds: extraction.skuFilters.subBrandIds,
                    },
                };
                return this.brandsService.getBrands(filterIds).pipe(
                    switchMap((data: BrandDto) => {
                        const brandSelections: Brand[] = [];
                        data.results.forEach((brand) => {
                            if (brand.subBrands) {
                                const filteredSubBrands: SubBrand[] = [];
                                brand.subBrands.forEach((subBrand) => {
                                    if (extraction.skuFilters.subBrandIds.some((value) => value == subBrand.subBrandId)) {
                                        filteredSubBrands.push(subBrand);
                                    }
                                });
                              brand.subBrands = filteredSubBrands;
                              brand.isParentBrandSelected = true;
                            }
                            brandSelections.push(brand);
                        });
                        return of(
                            extractionActions.getBrandsFiltersSuccess({
                                brands: brandSelections,
                            }),
                            extractionActions.navigateToFilters({ extractionId: extraction.id })
                        );
                    })
                );
            }),
            catchError((error: HttpErrorResponse) => {
                return of(
                    extractionActions.getFilterNamesFailure({
                        error,
                        filterType: 'brands',
                    })
                );
            })
        )
    );

    getCategoryFilterSelections$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.getFilterSelections),
            filter(({ extraction }) => extraction.skuFilters.categoryIds?.length > 0),
            switchMap(({ extraction }) => {
                const filterIds = {
                    filters: { categoryIds: extraction.skuFilters.categoryIds },
                };
                let allIndustryCategories: Array<IndustryCategory> = [];
                return this.categoriesService.getCategories(filterIds).pipe(
                    switchMap((data: CategoryDto) => {
                        allIndustryCategories = this.getIndustryCategory(extraction.skuFilters.categoryIds, data?.results);
                        const selectedCategories = (this.getExtractionSelectedCategories(data.results, allIndustryCategories) ?? []).map(
                            (category) => this.addCategoryBreadcrumbs(category)
                        );
                        return of(
                            extractionActions.getCategoriesFiltersSuccess({
                                categories: selectedCategories,
                            }),
                            extractionActions.navigateToFilters({ extractionId: extraction.id })
                        );
                    })
                );
            }),
            catchError((error: HttpErrorResponse) => {
                return of(
                    extractionActions.getFilterNamesFailure({
                        error,
                        filterType: 'categories',
                    })
                );
            })
        )
    );

    getCountryFilterSelections$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.getFilterSelections),
            filter(({ extraction }) => extraction.skuFilters.countryIds?.length > 0),
            switchMap(({ extraction }) => {
                const filterIds = {
                    filters: { countryIds: extraction.skuFilters.countryIds },
                };
                return this.countriesService.getCountries(filterIds).pipe(
                    switchMap((data: CountryDto) => {
                        const selectedCountries = data.results.filter((country) => extraction.skuFilters.countryIds.includes(country.id));
                        return of(
                            extractionActions.getCountriesFiltersSuccess({
                                countries: selectedCountries,
                            }),
                            extractionActions.navigateToFilters({ extractionId: extraction.id })
                        );
                    })
                );
            }),
            catchError((error: HttpErrorResponse) => {
                return of(
                    extractionActions.getFilterNamesFailure({
                        error,
                        filterType: 'countries',
                    })
                );
            })
        )
    );

    getRetailerFilterSelections$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.getFilterSelections),
            filter(({ extraction }) => extraction.skuFilters.retailerIds?.length > 0),
            switchMap(({ extraction }) => {
                const filterIds = {
                    filters: { retailerIds: extraction.skuFilters.retailerIds },
                };
                return this.retailersService.getRetailers(filterIds).pipe(
                    switchMap((data: RetailerDto) => {
                        return of(
                            extractionActions.getRetailerFiltersSuccess({
                                retailers: data.results,
                            }),
                            extractionActions.navigateToFilters({ extractionId: extraction.id })
                        );
                    })
                );
            }),
            catchError((error: HttpErrorResponse) => {
                return of(
                    extractionActions.getFilterNamesFailure({
                        error,
                        filterType: 'retailers',
                    })
                );
            })
        )
    );

    getRetailerTypeFilterSelections$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.getFilterSelections),
            filter(({ extraction }) => extraction.skuFilters.retailerTypeIds?.length > 0),
            switchMap(({ extraction }) => {
                const filterIds = {
                    filters: { retailerTypeIds: extraction.skuFilters.retailerTypeIds },
                };
                return this.retailerTypesService.getRetailerTypes(filterIds).pipe(
                    switchMap((data: RetailerTypeDto) => {
                        return of(
                            extractionActions.getRetailerTypeFiltersSuccess({
                                retailerTypes: data.results,
                            }),
                            extractionActions.navigateToFilters({ extractionId: extraction.id })
                        );
                    })
                );
            }),
            catchError((error: HttpErrorResponse) => {
                return of(
                    extractionActions.getFilterNamesFailure({
                        error,
                        filterType: 'retailerTypes',
                    })
                );
            })
        )
    );

    getSupplierFilterSelections$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.getFilterSelections),
            filter(({ extraction }) => extraction.skuFilters.supplierIds?.length > 0),
            switchMap(({ extraction }) => {
                const filterIds = {
                    filters: { supplierIds: extraction.skuFilters.supplierIds },
                };
                return this.suppliersService.getSuppliers(filterIds).pipe(
                    switchMap((data: SupplierDto) => {
                        return of(
                            extractionActions.getSuppliersFiltersSuccess({
                                suppliers: data.results,
                            }),
                            extractionActions.navigateToFilters({ extractionId: extraction.id })
                        );
                    })
                );
            }),
            catchError((error: HttpErrorResponse) => {
                return of(
                    extractionActions.getFilterNamesFailure({
                        error,
                        filterType: 'suppliers',
                    })
                );
            })
        )
    );

    getPromotionFilterSelections$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.getFilterSelections),
            filter(({ extraction }) => extraction.skuFilters.promotionTypeIds?.length > 0),
            switchMap(({ extraction }) => {
                const filterIds = {
                    filters: {
                        promotionIds: extraction.skuFilters.promotionTypeIds,
                        countryIds: extraction.skuFilters.countryIds,
                        categoryIds: extraction.skuFilters.categoryIds,
                    },
                };
                return this.promotionsService.getPromotions(filterIds).pipe(
                    switchMap((data: PromotionDto) => {
                        return of(
                            extractionActions.getPromotionFiltersSuccess({
                                promotions: data.results.filter((promotion) =>
                                    extraction.skuFilters.promotionTypeIds.includes(promotion.id)
                                ),
                            }),
                            extractionActions.navigateToFilters({ extractionId: extraction.id })
                        );
                    })
                );
            }),
            catchError((error: HttpErrorResponse) => {
                return of(
                    extractionActions.getFilterNamesFailure({
                        error,
                        filterType: 'promotions',
                    })
                );
            })
        )
    );

    getSubBrandSelections$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.addBrandsSuggestionSelection),
            switchMap(({ brands }) => {
                const filterIds = {
                    filters: {
                        brandIds: [brands[0].brandId],
                    },
                };
                return this.brandsService.getBrands(filterIds).pipe(
                    map((data: BrandDto) => {
                        const brands: Brand[] = [];
                        data.results.forEach((brand) => {
                            brand.isParentBrandSelected = true;
                            brands.push(brand);
                        });
                        return extractionActions.getSubBrandsSelectionSuccess({
                            brands,
                        });
                    })
                );
            }),
            catchError((error: HttpErrorResponse) => {
                return of(
                    extractionActions.getFilterNamesFailure({
                        error,
                        filterType: 'brands',
                    })
                );
            })
        )
    );

    getBrandsAndSubBrandSelections$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.addSubBrandsSuggestionSelection),
            switchMap(({ subBrands }) => {
                const filterIds = {
                    filters: {
                        subBrandIds: [subBrands[0].subBrandId],
                    },
                };
                return this.brandsService.getBrands(filterIds).pipe(
                    map((data: BrandDto) => {
                        const brands: Brand[] = [];
                        data.results.forEach((brand: Brand) => {
                            let subBrandsTemp: SubBrand[] | undefined = [];
                            subBrandsTemp = brand.subBrands?.filter((subBrand: SubBrand) => subBrand.subBrandId == subBrands[0].subBrandId);
                            if (subBrandsTemp) {
                                brand.subBrands = subBrandsTemp?.length > 0 ? subBrandsTemp : brand.subBrands;
                                brand.isParentBrandSelected = false;
                            }
                            brands.push(brand);
                        });
                        return extractionActions.getSubBrandsSelectionSuccess({
                            brands,
                        });
                    })
                );
            }),
            catchError((error: HttpErrorResponse) => {
                return of(
                    extractionActions.getFilterNamesFailure({
                        error,
                        filterType: 'brands',
                    })
                );
            })
        )
    );

    getSubCategoriesSelections$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.addCategoriesSuggestionSelection),
            switchMap(({ categories }) => {
                const filterIds = {
                    filters: {
                        categoryIds: [categories[0].id],
                    },
                };
                return this.categoriesService.getCategories(filterIds).pipe(
                    map((data: CategoryDto) => {
                        const categories: Category[] = [];
                        data.results.forEach((category) => {
                            categories.push(category);
                        });
                        return extractionActions.getSubCategoriesSelectionSuccess({
                            categories,
                        });
                    })
                );
            }),
            catchError((error: HttpErrorResponse) => {
                return of(
                    extractionActions.getFilterNamesFailure({
                        error,
                        filterType: 'categories',
                    })
                );
            })
        )
    );

    setIsLoading$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.isLoadingExtractions),
            map(() => {
                return extractionActions.fetchExtractions();
            })
        )
    );

    getExtractionWithNoFilters$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.getFilterNames),
            filter(
                ({ extraction }) =>
                    extraction.searchPhrase !== undefined &&
                    extraction.searchPhrase.length > 0 &&
                    Object.keys(extraction.skuFilters).length === 0
            ),
            map(({ extraction }) => {
                return extractionActions.isLoadingExtractionFilterNames({
                    extractionId: extraction.id,
                    isLoading: false,
                });
            })
        )
    );

    deleteExtraction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.deleteExtractions),
            mergeMap(({ extractionId }) => {
                return this.extractionService.deleteExtraction(extractionId).pipe(
                    mergeMap(() => {
                        return of(extractionActions.deleteExtractionsSuccess({ extractionId }));
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(extractionActions.deleteExtractionsFailure({ error }));
                    })
                );
            })
        )
    );

    downloadExtraction$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.downloadExtraction),
            mergeMap(({ extractionId, extractionName, fileType, includeAttributesFile }) => {
                return this.extractionService.downloadExtraction(extractionId).pipe(
                    mergeMap((blob: Blob) => {
                        const downloadURL = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = downloadURL;
                        link.download = `${extractionName}${this.getExtractionExtension(fileType, includeAttributesFile)}`;
                        link.click();
                        link.remove();
                        return of(extractionActions.downloadExtractionSuccess({ extractionId }));
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(extractionActions.downloadExtractionsFailure({ error, extractionId }));
                    })
                );
            })
        )
    );

    getExchangeRates$ = createEffect(() =>
        this.actions$.pipe(
            ofType(extractionActions.getExchangeRates),
            switchMap((currency) => {
                return this.exchangeRatesService.getCurrencyExchangeRates(currency.currency).pipe(
                    map((data: DynamicExchangeRate[]) => {
                        return extractionActions.getExchangeRatesSuccess({
                            exchangeRates: data,
                        });
                    }),
                    catchError((error: HttpErrorResponse) => {
                        return of(extractionActions.getExtractionsFailure({ error }));
                    })
                );
            })
        )
    );

    public getFilterIds(selectedFilters: Filters): SkuRequestFilter {
        return new SkuRequestFilter(
            selectedFilters.attributes.map((x) => x.id),
            selectedFilters.brands.filter((brand) => brand.isParentBrandSelected === true).map((x) => x.brandId),
            this.getSelectedCategoryIds(selectedFilters.categories),
            selectedFilters.countries.map((x) => x.id),
            selectedFilters.retailers.map((x) => x.id),
            selectedFilters.suppliers.map((x) => x.id),
            this.getSubBrandIds(selectedFilters.brands),
            selectedFilters.retailerTypes.map((x) => x.id),
            selectedFilters.attributeTypes.map((x) => x.id),
            selectedFilters.promotions.map((x) => x.id),
            this.getSelectedCategories(selectedFilters.categories),
            selectedFilters.firstSeenPriceRange,
            selectedFilters.lastSeenPriceRange,
            new MinMaxRange(selectedFilters.volumeRange?.min, selectedFilters.volumeRange?.max),
            new MinMaxRange(selectedFilters.weightRange?.min, selectedFilters.weightRange?.max),
            new MinMaxRange(selectedFilters.countRange?.min, selectedFilters.countRange?.max),
            new DateRange(selectedFilters.dateRange?.startDate, selectedFilters.dateRange?.endDate)
        );
    }

    private getSelectedCategories(
        categoryTree: Array<Category>,
        selectedCategories: Array<IndustryCategory> = []
    ): Array<IndustryCategory> {
        for (const category of categoryTree) {
            if (!category.childCategories || category.childCategories.length === 0) {
                const industryCategory = new IndustryCategory(category.id, category.industryCode);
                selectedCategories.push(industryCategory);
            } else {
                this.getSelectedCategories(category.childCategories, selectedCategories);
            }
        }
        return selectedCategories;
    }

    public getSubBrandIds(brands: Brand[]): Array<number> {
        const subBrandsList: SubBrand[] = [];
        brands.forEach((brand) => brand.subBrands?.forEach((subBrand) => subBrandsList.push(subBrand)));
        return subBrandsList.map((x) => x.subBrandId);
    }

    public getSelectedCategoryIds(categoryTree: Array<Category>, selectedCategories: Array<number> = []): Array<number> {
        for (const category of categoryTree) {
            if (!category.childCategories || category.childCategories.length === 0) {
                selectedCategories.push(category.id);
            } else {
                this.getSelectedCategoryIds(category.childCategories, selectedCategories);
            }
        }
        return selectedCategories;
    }

    private getExtractionSelectedCategoryNames(
        categories: Category[],
        extractions: Array<IndustryCategory>,
        selectedCategories: Array<string> = []
    ): Array<string> {
        for (const category of categories) {
            if (!category.childCategories || category.childCategories.length === 0) {
                if (extractions.some((extract) => extract.categoryId === category.id && extract.industryCode == category.industryCode)) {
                    selectedCategories.push(category.name);
                }
            } else {
                this.getExtractionSelectedCategoryNames(category.childCategories, extractions, selectedCategories);
            }
        }
        return selectedCategories;
    }

    private getExtractionSelectedCategories(categories: Category[], selectedCategories: IndustryCategory[]): Category[] | null {
        const relevantCategories = [];
        for (const category of categories) {
            if (
                selectedCategories.some(
                    (selectedCategory) =>
                        selectedCategory.categoryId === category.id && selectedCategory.industryCode == category.industryCode
                )
            ) {
                relevantCategories.push(category);
            } else if (category.childCategories) {
                const relevantChildren = this.getExtractionSelectedCategories(category.childCategories, selectedCategories);
                if (relevantChildren) {
                    const newCategory = { ...category, childCategories: relevantChildren };
                    relevantCategories.push(newCategory);
                }
            }
        }
        return relevantCategories.length > 0 ? relevantCategories : null;
    }

    private addCategoryBreadcrumbs(category: Category, breadcrumb?: string): Category {
        if (!category.childCategories || category.childCategories.length === 0) {
            return new Category(category.id, category.name, breadcrumb, undefined, undefined, category.industryCode, category.parentId);
        }
        const childCategories = [];
        for (const child of category.childCategories) {
            if (breadcrumb) {
                childCategories.push(this.addCategoryBreadcrumbs(child, `${breadcrumb} / ${category.name}`));
            } else {
                childCategories.push(this.addCategoryBreadcrumbs(child, category.name));
            }
        }
        return new Category(category.id, category.name, breadcrumb, childCategories, undefined, category.industryCode, category.parentId);
    }

    private getExtractionExtension(fileType: FileTypes, includeAttributesFile: boolean): string {
        switch (true) {
            case fileType === 'Excel':
                return '.xlsx';
            case fileType === 'CSV' && includeAttributesFile:
                return '.zip';
            default:
                return '.csv';
        }
    }

    private getIndustryCategory(
        categoryIds: Array<number>,
        extractions: Array<Category>,
        returnValue: Array<IndustryCategory> = []
    ): Array<IndustryCategory> {
        if (extractions?.length) {
            for (const category of extractions) {
                if (!category.childCategories || category.childCategories.length === 0) {
                    if (extractions.some((extract) => extract.id === category.id && extract.industryCode == category.industryCode)) {
                        returnValue.push({ categoryId: category.id, industryCode: category.industryCode } as IndustryCategory);
                    }
                } else {
                    this.getIndustryCategory(categoryIds, category.childCategories, returnValue);
                }
            }
        }
        return returnValue;
    }
}
